import { useState, useMemo } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import USAProductCard from "./components/USAProductCard";
import { usaProducts, usaCategories, usaSortOptions } from "@/mocks/usaProducts";
import SEO from "@/components/base/SEO";

const PRICE_RANGES = [
  { id: "all", label: "All Prices" },
  { id: "under25", label: "Under $25" },
  { id: "25to50", label: "$25 – $50" },
  { id: "50to100", label: "$50 – $100" },
  { id: "over100", label: "Over $100" },
];

const DISPATCH_FILTERS = [
  { id: "all", label: "Any Time" },
  { id: "sameday", label: "Same-Day Dispatch" },
  { id: "fast", label: "Fast (under 6h)" },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const usaShopSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "USA Shop — Verified USA Warehouse Products | StarBookPrime",
  "description": "Shop products from verified USA local warehouses. Same-day dispatch, 1–3 day delivery, no import delays. 100% verified American sellers.",
  "url": `${SITE_URL}/usa-shop`,
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "USA Shop", "item": `${SITE_URL}/usa-shop` }
    ]
  }
};

export default function USAShopPage() {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [sortBy, setSortBy] = useState("featured");
  const [priceRange, setPriceRange] = useState("all");
  const [dispatchFilter, setDispatchFilter] = useState("all");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    let list = [...usaProducts];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.title.toLowerCase().includes(q) || p.seller.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
    }
    if (activeCategory !== "All") list = list.filter((p) => p.category === activeCategory);
    if (verifiedOnly) list = list.filter((p) => p.verified);
    if (priceRange === "under25") list = list.filter((p) => p.price < 25);
    else if (priceRange === "25to50") list = list.filter((p) => p.price >= 25 && p.price <= 50);
    else if (priceRange === "50to100") list = list.filter((p) => p.price > 50 && p.price <= 100);
    else if (priceRange === "over100") list = list.filter((p) => p.price > 100);
    if (dispatchFilter === "sameday") list = list.filter((p) => p.dispatchHours <= 4);
    else if (dispatchFilter === "fast") list = list.filter((p) => p.dispatchHours < 6);

    if (sortBy === "price-low") list.sort((a, b) => a.price - b.price);
    else if (sortBy === "price-high") list.sort((a, b) => b.price - a.price);
    else if (sortBy === "rating") list.sort((a, b) => b.rating - a.rating);
    else if (sortBy === "newest") list.sort((a, b) => a.id.localeCompare(b.id));
    else if (sortBy === "dispatch") list.sort((a, b) => a.dispatchHours - b.dispatchHours);

    return list;
  }, [search, activeCategory, sortBy, priceRange, dispatchFilter, verifiedOnly]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#fafaf8" }}>
      <SEO
        title="USA Shop — Verified Local Warehouse, Fast Shipping | StarBookPrime"
        description="Shop direct from verified USA local warehouses. Same-day dispatch, 1–3 day delivery, no import delays. 100% verified American sellers — shop like Amazon."
        keywords="USA local warehouse shop, verified USA factory products, fast shipping USA"
        canonical="/usa-shop"
        ogImage="https://public.readdy.ai/ai/img_res/e5d48cf575f066c2dfe18bfb784d82d0.jpg"
        schema={usaShopSchema}
      />
      <Navbar />

      {/* Hero banner */}
      <div
        className="pt-20 pb-10 px-6 md:px-12 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #3D2000 0%, #7B4A00 40%, #B8860B 80%, #DAA520 100%)" }}
      >
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 30px, rgba(255,215,0,0.15) 30px, rgba(255,215,0,0.15) 60px)" }} />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wide" style={{ background: "rgba(255,215,0,0.2)", color: "#FFD700" }}>
                  <i className="ri-flag-line" /> USA Only
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.9)" }}>
                  <i className="ri-truck-line" /> Local Warehouse · Fast Shipping
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-2">
                USA Shop — Shop Like Amazon
              </h1>
              <p className="text-sm max-w-xl" style={{ color: "rgba(255,255,255,0.75)" }}>
                All products ship from verified USA warehouses. Same-day dispatch available. No import delays, no customs — just fast, reliable delivery.
              </p>
            </div>
            {/* Live stats */}
            <div className="flex gap-6 flex-shrink-0">
              {[
                { value: "18+", label: "Products", icon: "ri-store-2-line" },
                { value: "1–3", label: "Day Delivery", icon: "ri-truck-line" },
                { value: "100%", label: "USA Verified", icon: "ri-shield-check-line" },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-1" style={{ background: "rgba(255,215,0,0.15)" }}>
                    <i className={`${s.icon} text-lg`} style={{ color: "#FFD700" }} />
                  </div>
                  <div className="text-xl font-extrabold" style={{ color: "#FFD700" }}>{s.value}</div>
                  <div className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Search bar */}
          <div className="mt-6 relative max-w-2xl">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products, sellers, categories..."
              className="w-full pl-11 pr-4 py-3.5 rounded-2xl text-sm outline-none border-0"
              style={{ background: "rgba(255,255,255,0.95)", color: "#1a0f00" }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Category tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
          {usaCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
              style={
                activeCategory === cat.id
                  ? { background: "linear-gradient(135deg, #B8860B, #FFD700)", color: "#1a0f00" }
                  : { background: "white", color: "#6b7280", border: "1px solid #e5e7eb" }
              }
            >
              <i className={`${cat.icon} text-sm`} />
              {cat.label}
              <span
                className="text-xs px-1.5 py-0.5 rounded-full font-bold"
                style={
                  activeCategory === cat.id
                    ? { background: "rgba(61,32,0,0.2)", color: "#1a0f00" }
                    : { background: "#f3f4f6", color: "#9ca3af" }
                }
              >
                {cat.count}
              </span>
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-gray-500">
              <strong className="text-gray-900">{filtered.length}</strong> products found
            </span>
            {/* Verified toggle */}
            <button
              onClick={() => setVerifiedOnly(!verifiedOnly)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
              style={
                verifiedOnly
                  ? { background: "rgba(22,163,74,0.12)", color: "#16a34a", border: "1px solid rgba(22,163,74,0.3)" }
                  : { background: "white", color: "#6b7280", border: "1px solid #e5e7eb" }
              }
            >
              <i className="ri-shield-check-line" /> Verified Only
            </button>
            {/* Mobile filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap sm:hidden"
              style={{ background: "white", color: "#6b7280", border: "1px solid #e5e7eb" }}
            >
              <i className="ri-filter-3-line" /> Filters
            </button>
          </div>
          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 whitespace-nowrap">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm font-semibold px-3 py-2 rounded-xl border border-gray-200 bg-white outline-none cursor-pointer"
              style={{ color: "#374151" }}
            >
              {usaSortOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar filters */}
          <aside className={`flex-shrink-0 w-56 ${showFilters ? "block" : "hidden"} sm:block`}>
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-24 space-y-6">
              {/* Price range */}
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Price Range</h3>
                <div className="space-y-2">
                  {PRICE_RANGES.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setPriceRange(r.id)}
                      className="w-full text-left flex items-center gap-2 text-sm cursor-pointer transition-colors"
                      style={{ color: priceRange === r.id ? "#B8860B" : "#6b7280" }}
                    >
                      <div
                        className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                        style={{ borderColor: priceRange === r.id ? "#B8860B" : "#d1d5db" }}
                      >
                        {priceRange === r.id && <div className="w-2 h-2 rounded-full" style={{ background: "#B8860B" }} />}
                      </div>
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dispatch speed */}
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Dispatch Speed</h3>
                <div className="space-y-2">
                  {DISPATCH_FILTERS.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => setDispatchFilter(d.id)}
                      className="w-full text-left flex items-center gap-2 text-sm cursor-pointer transition-colors"
                      style={{ color: dispatchFilter === d.id ? "#B8860B" : "#6b7280" }}
                    >
                      <div
                        className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                        style={{ borderColor: dispatchFilter === d.id ? "#B8860B" : "#d1d5db" }}
                      >
                        {dispatchFilter === d.id && <div className="w-2 h-2 rounded-full" style={{ background: "#B8860B" }} />}
                      </div>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Trust badges */}
              <div className="pt-4 border-t border-gray-100 space-y-3">
                {[
                  { icon: "ri-shield-check-line", text: "All sellers verified" },
                  { icon: "ri-truck-line", text: "USA warehouse stock" },
                  { icon: "ri-secure-payment-line", text: "Buyer protection" },
                ].map((b) => (
                  <div key={b.text} className="flex items-center gap-2 text-xs text-gray-500">
                    <i className={b.icon} style={{ color: "#B8860B" }} />
                    {b.text}
                  </div>
                ))}
              </div>
            </div>
          </aside>

          {/* Product grid */}
          <div className="flex-1 min-w-0">
            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: "rgba(184,134,11,0.1)" }}>
                  <i className="ri-search-line text-2xl" style={{ color: "#B8860B" }} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">No products found</h3>
                <p className="text-sm text-gray-500 mb-4">Try adjusting your filters or search term</p>
                <button
                  onClick={() => { setSearch(""); setActiveCategory("All"); setPriceRange("all"); setDispatchFilter("all"); setVerifiedOnly(false); }}
                  className="px-5 py-2.5 rounded-full text-sm font-semibold cursor-pointer"
                  style={{ background: "linear-gradient(135deg, #B8860B, #FFD700)", color: "#1a0f00" }}
                >
                  Clear All Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filtered.map((product) => (
                  <USAProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bottom trust strip */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: "ri-shield-check-line", label: "100% Verified Sellers", sub: "Every factory audited & approved" },
            { icon: "ri-truck-line", label: "USA Local Warehouse", sub: "Ships in 1–3 business days" },
            { icon: "ri-refresh-line", label: "Easy Returns", sub: "30-day hassle-free returns" },
            { icon: "ri-customer-service-2-line", label: "24/7 Support", sub: "Real humans, fast responses" },
          ].map((b) => (
            <div key={b.label} className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-gray-100">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: "rgba(184,134,11,0.1)" }}>
                <i className={`${b.icon} text-lg`} style={{ color: "#B8860B" }} />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">{b.label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{b.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
